import { Button } from '@/components/design-system/Button';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { FormInput } from '@/features/form/inputs/FormInput';
import { FormPassword } from '@/features/form/inputs/FormPassword';
import { cn } from '@/utils/cn';
import { CircleX, Hash, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface VerificationCodeFormValues {
  code: string;
  password: string;
}

interface VerificationCodeFormProps {
  onSubmit: (data: VerificationCodeFormValues) => Promise<boolean>;
  loading?: boolean;
  className?: string;
  error?: AuthError | null;
}

export const VerificationCodeForm = ({ onSubmit, loading, className, error }: VerificationCodeFormProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit, watch } = useForm<VerificationCodeFormValues>({
    defaultValues: {
      code: '',
      password: ''
    }
  });

  const codeValue = watch('code');
  const passwordValue = watch('password');

  const codeError = error?.type === 'code';
  const passwordError = error?.type === 'password';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col items-center', className)}>
      <FormInput
        control={control}
        name="code"
        leftIcon={Hash}
        rightIcon={codeError ? CircleX : undefined}
        status={codeError ? 'error' : 'default'}
        placeholder="passwordResetSubmit.codePlaceholder"
        className="w-full"
        hideErrorMessage
      />

      <div className="mt-4 w-full">
        <FormPassword
          control={control}
          name="password"
          leftIcon={Lock}
          status={passwordError ? 'error' : 'default'}
          placeholder="passwordResetSubmit.newPasswordPlaceholder"
          autoComplete="new-password"
          hideErrorMessage
        />
      </div>

      <Button
        type="submit"
        loading={loading}
        disabled={!codeValue || !passwordValue || loading}
        className={cn(
          'mt-4 h-10 w-81 border text-base transition-all duration-200',
          loading && 'cursor-wait opacity-60',
          !codeValue || !passwordValue || loading
            ? 'cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400'
            : 'bg-primary-500 border-primary-500 hover:bg-primary-500/90 text-white hover:text-white/90'
        )}
      >
        {t('passwordResetSubmit.submit')}
      </Button>
    </form>
  );
};
